import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { CheckCircle, Clock, House, FileText, ChatCircleDots } from "@phosphor-icons/react";

const STATUS = {
  active: { label: "Active Lease", color: "text-emerald-700 bg-emerald-50" },
  pending_payment: { label: "Pending Payment", color: "text-amber-700 bg-amber-50" },
  cancelled: { label: "Cancelled", color: "text-red-700 bg-red-50" },
};

export default function MyRentalsPage() {
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/rentals/mine").then(({ data }) => setRentals(data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
      <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">Your homes</p>
      <h1 className="font-serif text-4xl lg:text-5xl text-midnight mb-10">My Rentals</h1>

      {loading ? (
        <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-40 bg-stone rounded-2xl animate-pulse" />)}</div>
      ) : rentals.length === 0 ? (
        <div className="text-center py-24 bg-white rounded-2xl border border-black/5" data-testid="no-rentals">
          <House size={64} weight="duotone" className="text-gold mx-auto mb-6" />
          <p className="font-serif text-2xl text-midnight mb-3">No rentals yet</p>
          <p className="text-neutral-500 mb-6">Find your next home on Zyro.</p>
          <Link to="/rentals" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-midnight text-bone hover:bg-midnight-hover">Browse rentals</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {rentals.map((r) => {
            const s = STATUS[r.status] || STATUS.pending_payment;
            return (
              <div key={r.id} className="bg-white border border-black/5 rounded-2xl overflow-hidden flex flex-col md:flex-row" data-testid={`rental-${r.id}`}>
                {r.property_image && <div className="md:w-64 h-48 md:h-auto overflow-hidden shrink-0"><img src={r.property_image} alt="" className="w-full h-full object-cover" /></div>}
                <div className="p-6 flex-1 flex flex-col md:flex-row md:items-center gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-serif text-xl text-midnight">{r.property_title}</h3>
                      <span className={`text-xs tracking-widest uppercase px-2 py-1 rounded-full ${s.color}`}>{s.label}</span>
                    </div>
                    <p className="text-sm text-neutral-500 mb-3">{r.property_city}</p>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div><p className="text-[10px] uppercase tracking-widest text-neutral-500">Move-in</p><p className="text-midnight">{new Date(r.start_date).toLocaleDateString("en-IN")}</p></div>
                      <div><p className="text-[10px] uppercase tracking-widest text-neutral-500">Lease</p><p className="text-midnight">{r.lease_months} months</p></div>
                      <div><p className="text-[10px] uppercase tracking-widest text-neutral-500">Rent</p><p className="text-midnight">₹{r.monthly_rent.toLocaleString("en-IN")}/mo</p></div>
                    </div>
                  </div>
                  <div className="text-right md:border-l md:pl-6 md:border-black/10">
                    <p className="text-[10px] uppercase tracking-widest text-neutral-500">Total paid</p>
                    <p className="font-serif text-2xl text-midnight">₹{r.total_amount.toLocaleString("en-IN")}</p>
                    {r.status === "pending_payment" && (
                      <Link to={`/rental-checkout/${r.id}`} className="inline-block mt-2 text-xs tracking-widest uppercase text-gold hover:text-midnight" data-testid={`pay-rental-${r.id}`}>Pay now →</Link>
                    )}
                    {r.status !== "pending_payment" && (
                      <div className="mt-3 flex flex-col gap-1 items-end">
                        <Link to={`/rentals/${r.id}/agreement`} className="text-xs tracking-widest uppercase text-gold hover:text-midnight inline-flex items-center gap-1" data-testid={`agreement-${r.id}`}>
                          <FileText size={12} weight="duotone" /> Agreement
                        </Link>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/rentals/${r.id}/invoice.pdf`} target="_blank" rel="noreferrer" className="text-xs tracking-widest uppercase text-gold hover:text-midnight inline-flex items-center gap-1" data-testid={`invoice-${r.id}`}>
                          <FileText size={12} weight="duotone" /> Invoice
                        </a>
                        <Link to={`/chat/${r.id}`} className="text-xs tracking-widest uppercase text-gold hover:text-midnight inline-flex items-center gap-1" data-testid={`chat-${r.id}`}>
                          <ChatCircleDots size={12} weight="duotone" /> Chat
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
